import React, { useState, useEffect } from "react";
import classnames from "classnames";
import { useStore, StoreTypes } from "context";
import * as actionTypes from "constants/actionTypes";
import { BookFlipType } from "constants/flipTypes";
import CommonDragModal from "components/common/DragModal";
import BookPage from "components/BookPage";
import { InteractiveObjectView } from "components/InteractiveObjectView";
import { API } from "api";
import { replaceBookPage } from "util/book";
import styles from "./index.module.scss";

const InitInteractiveObjectState = { version: 0, state: [] };

// 從 SVG 的 viewBox 屬性解析頁面長寬比（width / height），供彈窗響應式尺寸計算使用
const parsePageAspectRatio = (svgString) => {
  if (!svgString) return null;
  const match = svgString.match(/viewBox="([^"]+)"/i);
  if (!match) return null;
  const parts = match[1].trim().split(/\s+/).map(Number);
  if (parts.length < 4 || !parts[3]) return null;
  return parts[2] / parts[3];
};

const LicensingDialog = () => {
  const [{ licensingDialog: { copyrightSrcs } }, globalDispatch] = useStore(StoreTypes.global);
  const [{ books, bookId }] = useStore(StoreTypes.books);

  const [pageData, setPageData] = useState([]);
  const [interactiveObjectJSON, setInteractiveObjectJSON] = useState({});
  const [interactiveObjectSVG, setInteractiveObjectSVG] = useState({});
  const [interactiveObjectState, setInteractiveObjectState] = useState(InitInteractiveObjectState);

  const book = books && books.find(b => b.bookId === bookId);

  useEffect(() => {
    if (!book || !copyrightSrcs || !copyrightSrcs.length) return;

    const pageUrl = `${process.env.REACT_APP_BOOK_CDN_DOMAIN}/uploadoutput/${bookId}/`;
    const copyrightPageInfos = copyrightSrcs
      .map(src => book.pageInfos && book.pageInfos.find(p => p.html === src))
      .filter(Boolean);

    if (!copyrightPageInfos.length) return;

    const pageIndices = copyrightPageInfos.map(p => p.pageIndex);

    const fetchPages = Promise.all(
      copyrightPageInfos.map(async page => {
        const str = await API.getText(pageUrl + page.html);
        return replaceBookPage({ page, pageUrl, str });
      })
    );

    const fetchInteractiveObjects = book.interactiveObjectId
      ? Promise.all(
          pageIndices.map(async pageIndex => {
            try {
              const result = await API.getJSON(
                `${process.env.REACT_APP_RESOURCE_CDN_DOMAIN}/interactiveObjects/${book.interactiveObjectId}/${pageIndex}.dat`
              );
              return result.rawData;
            } catch {
              return null;
            }
          })
        )
      : Promise.resolve([]);

    Promise.all([fetchPages, fetchInteractiveObjects]).then(([pages, ioResults]) => {
      setPageData(pages);

      const ioJSON = {};
      const ioSVG = {};
      ioResults.filter(Boolean).forEach(({ pageIndex, json, svg }) => {
        ioJSON[pageIndex] = json.reduce((acc, v) => { acc[v.id] = v; return acc; }, {});
        ioSVG[pageIndex] = svg;
      });
      setInteractiveObjectJSON(ioJSON);
      setInteractiveObjectSVG(ioSVG);
    });
  }, [book, bookId, copyrightSrcs]);

  const svgAspectRatio = pageData.length > 0 ? parsePageAspectRatio(pageData[0].svg) : null;
  const pageAspectRatio = svgAspectRatio !== null
    ? svgAspectRatio
    : (book && book.height ? book.width / book.height : null);
  const contentAspectRatio = pageAspectRatio !== null ? pageAspectRatio * pageData.length : null;

  const handleClose = () => {
    globalDispatch({ type: actionTypes.CLOSE_LICENSING_DIALOG });
  };

  const content = pageData.length ? (
    <div className={classnames(styles.copyrightPages, { [styles.reverse]: book && book.LRFlip === BookFlipType.RIGHT_TO_LEFT })}>
      {pageData.map(page => (
        <div key={page.pageIndex} className={styles.copyrightPage}>
          <BookPage
            content={page.svg}
            bookInfo={book}
            pageIndex={page.pageIndex}
          />
          {interactiveObjectSVG[page.pageIndex] && (
            <InteractiveObjectView
              svgString={interactiveObjectSVG[page.pageIndex]}
              pageIndex={page.pageIndex}
              bookInfo={book}
              interactiveObjectState={interactiveObjectState}
              setInteractiveObjectState={setInteractiveObjectState}
              interactiveObjectJSON={interactiveObjectJSON}
            />
          )}
        </div>
      ))}
    </div>
  ) : null;

  return (
    <CommonDragModal
      title=""
      showCloseButton
      onCloseButtonClick={handleClose}
      onMaskClick={handleClose}
      headerColor="#315d83"
      bodyColor="#D9F1F8"
      draggable={false}
      maskType="overlay"
      modalClassName={styles.licensingModal}
      contentClassName={styles.licensingContent}
      modalStyle={{
        '--close-btn-size': 'calc((50vmin / 900) * 100)',
        ...(contentAspectRatio !== null ? { '--content-ar': contentAspectRatio } : {}),
      }}
      content={content}
      bottom={null}
    />
  );
};

export default LicensingDialog;
